import React, { Fragment, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Table, Spinner, Alert, Badge, Nav, NavItem, NavLink, TabContent, TabPane, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useBulkPreviewMutation, useBulkApplyMutation, useGetBulkHistoryQuery } from '../../../../store/services/inventoryApi';
import { toast } from 'react-toastify';
import { Upload, FileText, CheckCircle, AlertTriangle, Download, Trash2, Clock, HelpCircle } from 'react-feather';
import * as XLSX from 'xlsx';
import moment from 'moment';

const BulkUpload = () => {
    const [activeTab, setActiveTab] = useState('1');
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [instructionsOpen, setInstructionsOpen] = useState(false);

    const toggleInstructions = () => setInstructionsOpen(!instructionsOpen);

    const [bulkPreview, { isLoading: isPreviewing }] = useBulkPreviewMutation();
    const [bulkApply, { isLoading: isApplying }] = useBulkApplyMutation();

    // History query
    const { data: historyData, isLoading: isLoadingHistory, refetch: refetchHistory } = useGetBulkHistoryQuery({}, { skip: activeTab !== '2' });

    const handleFileUpload = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            parseFile(selectedFile);
        }
    };

    const parseFile = (file) => {
        setIsParsing(true);
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                if (jsonData.length === 0) {
                    toast.error("File is empty or invalid format");
                    setIsParsing(false);
                    return;
                }

                const items = jsonData.map(row => ({
                    productVariantId: row.productVariantId || row['Product Variant ID'] || row.variantId,
                    storeId: row.storeId || row['Store ID'],
                    action: (row.action || row.Action || 'SET').toUpperCase(),
                    quantity: parseInt(row.quantity || row.Quantity || 0),
                    reason: row.reason || row.Reason || 'Bulk Upload'
                }));

                const result = await bulkPreview({ items, fileName: file.name }).unwrap();
                setPreviewData(result.data || result);
                toast.success("File parsed and validated");
            } catch (err) {
                console.error("Parsing error", err);
                toast.error(err?.data?.message || "Failed to parse file. Ensure column names match: productVariantId, storeId, action, quantity, reason");
            } finally {
                setIsParsing(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleApply = async () => {
        if (!previewData || previewData.hasErrors) {
            toast.error("Please resolve errors before applying");
            return;
        }

        try {
            const response = await bulkApply({ items: previewData.items, fileName: file.name }).unwrap();
            const result = response?.data || response;

            const succeeded = result?.success ?? 0;
            const failed = result?.failed ?? 0;

            if (failed === 0) {
                toast.success(`Bulk changes applied successfully — ${succeeded} row(s) updated`);
                setFile(null);
                setPreviewData(null);
            } else if (succeeded > 0) {
                toast.warning(`Partially applied — ${succeeded} succeeded, ${failed} failed`);
                if (result?.errors?.length > 0) {
                    result.errors.forEach(e => toast.error(`Row ${e.row}: ${e.error}`, { autoClose: 6000 }));
                }
                setFile(null);
                setPreviewData(null);
            } else {
                toast.error(`All ${failed} row(s) failed to apply. Check the errors and re-upload.`);
                if (result?.errors?.length > 0) {
                    result.errors.forEach(e => toast.error(`Row ${e.row}: ${e.error}`, { autoClose: 6000 }));
                }
            }
        } catch (err) {
            toast.error(err?.data?.message || "Failed to apply changes — server error");
        } finally {
            // Always refresh history silently — must not be inside try or its failure triggers the catch
            try { refetchHistory(); } catch (_) {}
        }
    };

    const downloadTemplate = () => {
        const templateData = [
            { productVariantId: 'ID_HERE', storeId: 'ID_HERE', action: 'SET/INCREASE/DECREASE', quantity: 10, reason: 'Initial Stock' }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "GoBooze_Bulk_Upload_Template.csv", { bookType: "csv" });
    };

    const clearFile = () => {
        setFile(null);
        setPreviewData(null);
    };

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Bulk Inventory" parent="Inventory" title="Bulk Upload" />

            <Modal isOpen={instructionsOpen} toggle={toggleInstructions} size="lg">
                <ModalHeader toggle={toggleInstructions}>Bulk Upload Instructions</ModalHeader>
                <ModalBody>
                    <h6 className="mb-2">How to Perform Mass Inventory Updates</h6>
                    <p className="text-muted mb-3">Follow these steps to successfully import stock changes in bulk:</p>
                    <ul className="ps-3 mb-4">
                        <li className="mb-2">Click the <strong>Template</strong> button to download the boilerplate CSV file.</li>
                        <li className="mb-2">Open the downloaded template in Excel, Numbers, Google Sheets, or a text editor.</li>
                        <li className="mb-2">Fill in your inventory adjustment records. Each row represents a single product variant's stock change in a specific store.</li>
                        <li className="mb-2">Save or export the file in <strong>CSV (Comma-separated values)</strong> format.</li>
                        <li className="mb-2">Select or drop your saved file in the upload zone to validate the records.</li>
                        <li className="mb-2">Review any errors in the preview panel, and once corrected, click <strong>Confirm & Apply</strong>.</li>
                    </ul>

                    <h6 className="border-bottom pb-2">CSV Column Specifications</h6>
                    <Table responsive bordered size="sm" className="mt-2 text-xs">
                        <thead className="table-light">
                            <tr>
                                <th>Column Name</th>
                                <th>Required</th>
                                <th>Description & Format</th>
                                <th>Example Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>productVariantId</strong></td>
                                <td><Badge color="danger">Yes</Badge></td>
                                <td>24-character MongoDB hexadecimal ID of the Product Variant.</td>
                                <td><code>60c72b2f9b1d8a23d4cf231a</code></td>
                            </tr>
                            <tr>
                                <td><strong>storeId</strong></td>
                                <td><Badge color="danger">Yes</Badge></td>
                                <td>24-character MongoDB hexadecimal ID of the Store destination.</td>
                                <td><code>60c72b2f9b1d8a23d4cf235f</code></td>
                            </tr>
                            <tr>
                                <td><strong>action</strong></td>
                                <td><Badge color="danger">Yes</Badge></td>
                                <td>Stock operation. Allowed values: <code>SET</code> (overwrites quantity), <code>INCREASE</code> (increments), or <code>DECREASE</code> (decrements).</td>
                                <td><code>INCREASE</code></td>
                            </tr>
                            <tr>
                                <td><strong>quantity</strong></td>
                                <td><Badge color="danger">Yes</Badge></td>
                                <td>Positive integer representing the count of items to adjust.</td>
                                <td><code>15</code></td>
                            </tr>
                            <tr>
                                <td><strong>reason</strong></td>
                                <td><Badge color="secondary">No</Badge></td>
                                <td>Brief notes about this adjustment (max 200 chars). Defaults to "Bulk Upload".</td>
                                <td><code>Damaged Stock replacement</code></td>
                            </tr>
                        </tbody>
                    </Table>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleInstructions}>Close</Button>
                </ModalFooter>
            </Modal>

            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center pb-0">
                                <h5>Mass Inventory Updates</h5>
                                <div className="d-flex gap-2">
                                    <Button color="info" outline size="sm" onClick={downloadTemplate}>
                                        <Download size={14} className="me-1" /> Template
                                    </Button>
                                    <Button color="primary" outline size="sm" onClick={toggleInstructions}>
                                        <HelpCircle size={14} className="me-1" /> How to Upload
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Nav tabs className="border-bottom mb-4">
                                    <NavItem>
                                        <NavLink className={activeTab === '1' ? 'active' : ''} onClick={() => setActiveTab('1')} style={{ cursor: 'pointer' }}>
                                            <Upload size={16} className="me-2" /> Upload New
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink className={activeTab === '2' ? 'active' : ''} onClick={() => setActiveTab('2')} style={{ cursor: 'pointer' }}>
                                            <Clock size={16} className="me-2" /> Upload History
                                        </NavLink>
                                    </NavItem>
                                </Nav>

                                <TabContent activeTab={activeTab}>
                                    <TabPane tabId="1">
                                        {!file ? (
                                            <div className="text-center p-5 border-dashed rounded" style={{ border: '2px dashed #eee', backgroundColor: '#fafafa' }}>
                                                <Upload size={48} className="text-muted mb-3" />
                                                <h4>Upload Inventory File</h4>
                                                <p className="text-muted">Drop your CSV or Excel file here or click to browse</p>
                                                <input
                                                    type="file"
                                                    id="bulk-file"
                                                    hidden
                                                    onChange={handleFileUpload}
                                                    accept=".csv, .xlsx, .xls"
                                                />
                                                <Button color="primary" onClick={() => document.getElementById('bulk-file').click()}>
                                                    Select File
                                                </Button>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
                                                    <div className="d-flex align-items-center">
                                                        <FileText size={24} className="text-primary me-3" />
                                                        <div>
                                                            <h6 className="mb-0">{file.name}</h6>
                                                            <small className="text-muted">{(file.size / 1024).toFixed(2)} KB</small>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <Button color="danger" outline size="sm" onClick={clearFile} disabled={isApplying}>
                                                            <Trash2 size={14} className="me-1" /> Remove
                                                        </Button>
                                                        <Button color="success" size="sm" onClick={handleApply} disabled={isApplying || previewData?.hasErrors}>
                                                            {isApplying ? <Spinner size="sm" /> : <><CheckCircle size={14} className="me-1" /> Confirm & Apply</>}
                                                        </Button>
                                                    </div>
                                                </div>

                                                {isParsing || isPreviewing ? (
                                                    <div className="text-center p-5">
                                                        <Spinner color="primary" />
                                                        <p className="mt-2 text-muted">Validating data...</p>
                                                    </div>
                                                ) : previewData ? (
                                                    <div className="table-responsive">
                                                        {previewData.hasErrors && (
                                                            <Alert color="danger" className="py-2">
                                                                <AlertTriangle size={18} className="me-2" />
                                                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Found errors in the file. Please correct them and re-upload.
                                                            </Alert>
                                                        )}
                                                        <Table bordered hover size="sm">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>#</th>
                                                                    <th>Product/Variant</th>
                                                                    <th>Store</th>
                                                                    <th>Action</th>
                                                                    <th>Qty</th>
                                                                    <th>Change</th>
                                                                    <th>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {previewData.items?.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td>{index + 1}</td>
                                                                        <td>
                                                                            <div className="fw-bold">{item.productName || item.productVariantId}</div>
                                                                            <small className="text-muted">SKU: {item.sku || 'N/A'}</small>
                                                                        </td>
                                                                        <td>{item.storeName || item.storeId}</td>
                                                                        <td>
                                                                            <Badge color={item.action === 'SET' ? 'info' : item.action === 'INCREASE' ? 'success' : 'warning'}>
                                                                                {item.action}
                                                                            </Badge>
                                                                        </td>
                                                                        <td className="fw-bold">{item.quantity}</td>
                                                                        <td>
                                                                            {item.stockBefore !== undefined ? (
                                                                                <span className="text-muted">{item.stockBefore} → </span>
                                                                            ) : null}
                                                                            <span className="text-primary fw-bold">{item.stockAfter ?? item.quantity}</span>
                                                                        </td>
                                                                        <td>
                                                                            {item.error ? (
                                                                                <Badge color="danger">{item.error}</Badge>
                                                                            ) : (
                                                                                <Badge color="success">Valid</Badge>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                    </TabPane>

                                    <TabPane tabId="2">
                                        {isLoadingHistory ? (
                                            <div className="text-center p-5"><Spinner color="primary" /></div>
                                        ) : (
                                            <div className="table-responsive">
                                                <Table hover>
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>File Name</th>
                                                            <th>Type</th>
                                                            <th>Totals</th>
                                                            <th>Performed By</th>
                                                            <th>Result</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {historyData?.data?.data?.map((log) => (
                                                            <tr key={log._id}>
                                                                <td>{moment(log.createdAt).format('DD MMM YYYY HH:mm')}</td>
                                                                <td><FileText size={14} className="me-1 text-primary" /> {log.fileName}</td>
                                                                <td><Badge color="secondary">{log.operationType}</Badge></td>
                                                                <td>{log.totalRows} rows</td>
                                                                <td>{log.performedBy?.email}</td>
                                                                <td>
                                                                    <Badge color="success" className="me-1">{log.successCount} Success</Badge>
                                                                    {log.failedCount > 0 && <Badge color="danger">{log.failedCount} Failed</Badge>}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {(!historyData?.data?.data || historyData.data.data.length === 0) && (
                                                            <tr><td colSpan="6" className="text-center p-4 text-muted">No history found</td></tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default BulkUpload;
